/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, type FC } from 'react';
import type { ContentProps, TriggerProps } from '@atlaskit/popup/types';
import type { EntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import { useOpenable } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-openable/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { OnCloseProps, OnClose, CustomTriggerComponentType } from '../../types.tsx';
import { MenuTriggerButton } from './MenuTriggerButton.tsx';

export type MenuContainerProps = OnCloseProps & {
	content: (props: ContentProps) => React.ReactNode;
	CustomTrigger?: CustomTriggerComponentType;
	onClick?: () => void;
	buttonTriggerRef?: EntryPointButtonTrigger;
	shouldRenderToParent?: boolean;
	projectName?: string;
	actionMenuId?: string;
};
export const MenuContainer: FC<MenuContainerProps> = ({
	content,
	CustomTrigger,
	onClick,
	onClose: onCloseCustom,
	buttonTriggerRef,
	shouldRenderToParent = true,
	projectName,
	actionMenuId,
}: MenuContainerProps) => {
	const { isOpen, onClose, onToggle } = useOpenable();
	const route = useCurrentRoute();

	const handleClose = useCallback<OnClose>(
		(e) => {
			try {
				onCloseCustom?.(e);
			} finally {
				onClose();
			}
		},
		[onCloseCustom, onClose],
	);

	const onClickHandler = useCallback(() => {
		onToggle();
		onClick?.();
	}, [onClick, onToggle]);

	const trigger = (triggerProps: TriggerProps) => {
		const { ref, ...restTriggerProps } = triggerProps;

		return CustomTrigger ? (
			<CustomTrigger
				isSelected={isOpen}
				onClick={onClickHandler}
				ref={(element: HTMLButtonElement) => {
					buttonTriggerRef && buttonTriggerRef(element);
					if (typeof ref === 'function') {
						ref(element);
					}
				}}
				{...restTriggerProps}
			/>
		) : (
			<MenuTriggerButton
				{...(fg('jsm_aiiy_for_nav_item_sidebar') ? { name: projectName } : {})}
				actionMenuId={actionMenuId}
				isSelected={isOpen}
				onClick={onClickHandler}
				ref={(element: HTMLButtonElement) => {
					buttonTriggerRef && buttonTriggerRef(element);
					if (typeof ref === 'function') {
						ref(element);
					}
				}}
				{...restTriggerProps}
			/>
		);
	};

	return (
		<Popup
			content={content}
			isOpen={isOpen}
			onClose={handleClose}
			placement="bottom-start"
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="navigation-project-action-menu.ui.menu-container.popup"
			messageType="transactional"
			trigger={trigger}
			shouldRenderToParent={route.name.includes('legacy') ? false : shouldRenderToParent}
			strategy="absolute"
		/>
	);
};
